import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { balanceToneClass, balanceChipClass } from '../../../lib/helpers/balance';
import { UserContext } from "../../../context/UserContext";
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import apiFetch, { safeJson, apiErrorDetail } from "../../../context/api";
import MemberPhoto from "../../Elements/MemberPhoto";
import CartRow from "../../Elements/CartRow";
import CartBar from "../../Elements/CartBar";
import Button from "../../Elements/Button";
import CustomModal from "../../Elements/CustomModal";
import { PILDORA, PILDORA_ACTIVA, PILDORA_INACTIVA } from "../ProductsComponents/PildorasCategoria";
import { eur as eurSistema } from "../../Elements/CartFooter";
import CaducadoChip from "../../Elements/CaducadoChip";
import { busquedaSuficiente, filtroBusqueda, getImageUrl, socioNum } from "../../../lib/helpers";
// (`hayBascula`/`notaPedidoMerma` los usa CartRow por dentro: la báscula de la
// línea ya no se pinta aquí.)
import { esPeso, mermaDe, unidadDe } from "../../../lib/helpers/products";
import useMermaTolerancia from "../../../lib/hooks/useMermaTolerancia";
import { resolveMemberDiscount } from "../../../lib/helpers/memberDiscount";
import { effectiveUnitPrice } from "../../../lib/helpers/effectivePrice";
import { calcularTotalesCarrito } from "../../../lib/cartTotals";
import notFoundImage from "../../../assets/fox-not-found.webp";
import AddCashModal from "../../Elements/AddCashModal";
import NotaSocioPopup, { notaConAviso, tieneAvisoSocio } from "../../Elements/NotaSocioPopup";
import QRCode from "react-qr-code";
import { RefreshCw, Smartphone } from "lucide-react";
import EntryTimer from "../ControlComponents/EntryTimer";
import ClientDetailModal from "../CustomerComponents/ClientDetailModal";
import "./ClubEntradas.css";

/**
 * Entradas del CLUB SOCIAL (CRUO F1) — página propia, separada del Control del
 * POS de cannabis (docs/ARQUITECTURA_CARRITO_V2.md §estrangulamiento).
 *
 * Principios que arreglan el "socio atascado" del flujo anterior:
 *  - UNA sola fuente de verdad: `atendiendo` (el socio seleccionado). Sin URL-sync,
 *    sin localStorage, sin estado espejo en un embed.
 *  - Carrito = Cart v2 NATIVO (/v1/cart, context pos_productos) con CAS: el carrito
 *    vive en el servidor por socio; cambiar de socio nunca pierde nada.
 *  - "Enviar a Barra" convierte el carrito en comanda (checkout-to-order).
 */

const CONTEXT = "pos_productos";

// La nota de línea la lee la COCINA en el ticket de papel ("sin cebolla", "muy
// hecho", "sin gluten"). 200 es el ancho exacto de `cart_items.notes`
// (String(200) en models.py): el campo corta AQUÍ para que no corte el servidor.
const NOTA_MAX = 200;

function eur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

// ── Productos por peso ──────────────────────────────────────────────────────
// `esPeso`/`unidadDe` viven en lib/helpers/products: la carta, la barra y el
// carrito tienen que responder lo mismo a "¿esto va por gramos?".
//
// Esta carta los trataba como unidades: pintaba "€/u", el campo solo aceptaba
// enteros de unidades y "+ Añadir" con el campo vacío metía un 1 — que en un
// producto por peso es UN GRAMO cobrado al precio del gramo. El QUESO CABRA de
// Test CRUO (5 €/g) se cobraba a 5 € pusieras lo que pusieras. Reporte CRUO,
// 19-ago-2026.
//
// Los gramos aceptan DECIMALES (3 posiciones) desde la tanda 23-08: la columna
// `order_items.quantity` es Numeric desde U0 (migración cmdpeso0qty1) y
// `_as_order_quantity` los admite en productos a peso. La caja de la tarjeta es
// la misma que la del dispensario (Peso ↔ Euro enlazados + Báscula opcional):
// «el usuario tiene que ver lo mismo que en dispensario» (petición CRUO 23-08).
//
// El `unit_type` viaja EN la línea del carrito (`_unit_type`, como `_name`) en vez
// de resolverse mirando el catálogo: los productos se cargan paginados y aparte del
// carrito, así que una línea pintada antes de que termine esa carga —o de un
// producto ya retirado— salía como unidades, con sus −/+, y un toque escribía 249 g
// donde había 250. Como el guardado NO reescribe las líneas (la pantalla manda), el
// sello dura toda la sesión.
//
// Primero se mira `unit_type` a secas: hoy `/v1/cart` no lo devuelve, pero el día
// que lo haga es la fuente buena y cierra también el carrito rehidratado con
// `loadCart`, que es el único hueco que le queda a esta cadena.
const unitTypeDeLinea = (it, producto) =>
  it?.unit_type ?? it?._unit_type ?? producto?.unit_type ?? null;

// Una línea del carrito ES el par (producto, ubicación): es la clave con la que el
// servidor fusiona líneas (`_merge_lines`) y la misma del `key` de React. La nota
// se ata al par, no al producto, así que el mismo producto sacado de dos neveras
// puede llevar dos notas distintas sin pisarse.
/**
 * La línea tal y como viaja al servidor. UN solo sitio.
 *
 * Había DOS: el guardado normal y el que se hace justo antes de «Enviar a Barra». Al
 * añadir el «Dto. %» se puso solo en el primero, así que el segundo —que corre después
 * y SOBRESCRIBE el carrito— lo borraba, y el descuento no llegaba a la Barra. Reportado
 * por el club el 31-08, en caliente.
 *
 * Dos constructores de la misma carga es un campo nuevo perdido cada vez.
 */
const lineaParaElServidor = (it) => ({
  product_id: it.product_id,
  quantity: it.quantity,
  unit_price: it.unit_price,
  real_weight: it.real_weight ?? null,
  location_id: it.location_id || null,
  notes: it.notes || null,
  discount_pct: it.discount_pct != null && it.discount_pct !== ""
    ? Number(it.discount_pct)
    : null,
});

const lineaKey = (it) => `${it.product_id}-${it.location_id || ""}`;

function NumSocioChip({ socio }) {
  const n = socioNum(socio);
  if (n == null) return null;
  return (
    <span className="rounded bg-[var(--muted)] px-2 py-0.5 font-medium text-[var(--muted-foreground)]">
      Nº Socio: {n}
    </span>
  );
}

// El estado «En sala» con el mismo verde de éxito que el semáforo de Entradas
// (auditoría CRUO 21-09, CRUO-006: antes un #DCFCE7/#16A34A a mano, dos veces).
function EnSalaChip() {
  return (
    <span className="inline-flex flex-shrink-0 rounded-full bg-[var(--toast-success-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--success)]">
      En sala
    </span>
  );
}

export default function ClubEntradas() {
  const [token, , , venueID] = useContext(UserContext);
  const {
    usersInVenue,
    markUserEntered,
    markUserLeft,
    refresh: refreshSala,
    updateUserCredit,
    updateUser,
  } = useInVenueUsers();

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  // ── sala ──────────────────────────────────────────────────────────────────
  const [salaSearch, setSalaSearch] = useState("");
  const [atendiendo, setAtendiendo] = useState(null); // socio seleccionado (objeto de sala)
  const [avisoSocio, setAvisoSocio] = useState(null); // socio con nota marcada "abrir al interactuar"
  const [msg, setMsg] = useState(null); // {tone, text}

  const salaFiltrada = useMemo(() => {
    if (!salaSearch.trim()) return usersInVenue;
    const coincide = filtroBusqueda(salaSearch);
    return usersInVenue.filter((u) =>
      coincide([u?.profile?.first_name, u?.profile?.last_name, u?.email, socioNum(u)])
    );
  }, [usersInVenue, salaSearch]);

  // Si el socio atendido sale del club, se suelta solo (sin bucles: estado único).
  useEffect(() => {
    if (atendiendo && !usersInVenue.some((u) => String(u.id) === String(atendiendo.id))) {
      setAtendiendo(null);
    }
  }, [usersInVenue, atendiendo]);

  // ── productos ─────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState("");
  const [categoria, setCategoria] = useState("Todo");

  useEffect(() => {
    if (!token || !venueID) return;
    let cancelled = false;
    (async () => {
      try {
        const PAGE = 200;
        let skip = 0;
        let all = [];
        for (;;) {
          const res = await apiFetch(
            `/v1/get-venue-products?exclude_dispensario=true&skip=${skip}&limit=${PAGE}`,
            { headers: { ...authHeaders, "X-Venue-ID": venueID } }
          );
          if (!res.ok) throw new Error("No se pudieron cargar los productos");
          const batch = await safeJson(res);
          all = all.concat(Array.isArray(batch) ? batch : []);
          if (!Array.isArray(batch) || batch.length < PAGE) break;
          skip += PAGE;
        }
        if (!cancelled) setProducts(all.filter((p) => !p.is_dispensario));
      } catch (e) {
        if (!cancelled) setMsg({ tone: "err", text: e.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, venueID, authHeaders]);

  const categorias = useMemo(() => {
    const set = new Set();
    for (const p of products) if (p?.category?.name) set.add(p.category.name);
    return ["Todo", ...Array.from(set).sort()];
  }, [products]);

  const productosVisibles = useMemo(() => {
    const q = prodSearch.trim().toLowerCase();
    return products.filter(
      (p) =>
        (categoria === "Todo" || p?.category?.name === categoria) &&
        (!q || (p.name || "").toLowerCase().includes(q))
    );
  }, [products, categoria, prodSearch]);

  // ── mesas (service_tables): para "servir en mesa" desde el staff ──────────
  const [mesas, setMesas] = useState([]);
  const [mesaId, setMesaId] = useState(""); // "" = recoge en barra

  useEffect(() => {
    if (!token || !venueID) return;
    (async () => {
      try {
        const res = await apiFetch(`/v1/service-tables?venue_id=${venueID}`, { headers: authHeaders });
        if (res.ok) setMesas((await safeJson(res)) || []);
      } catch {
        /* sin mesas no pasa nada: todo será recogida en barra */
      }
    })();
  }, [token, venueID, authHeaders]);

  // ── carrito (Cart v2, servidor = fuente de verdad) ────────────────────────
  const [cart, setCart] = useState({ items: [], version: 0 });
  const [cartBusy, setCartBusy] = useState(false);
  // Cantidad a añadir por tarjeta, como en el dispensario: vacío = 1, para que el
  // gesto de siempre no cambie (petición del usuario, 17-ago).
  const [addQty, setAddQty] = useState({});
  // Línea cuya nota está abierta para escribir (clave de `lineaKey`). Solo controla
  // si se ve el campo o el botón "+ nota": una línea que YA tiene nota enseña el
  // campo siempre, para que la nota se lea sin abrir nada.
  const [notaAbierta, setNotaAbierta] = useState(null);
  // Lo tecleado a medias en los gramos de una línea («0», «2.», «2,50»): se enseña
  // tal cual hasta que es un número válido en su forma canónica, y entonces manda la
  // cantidad del carrito. Cada borrador recuerda la cantidad de la línea con la que
  // se escribió: si la cantidad cambia por otro camino (la tarjeta, el otro campo,
  // el servidor), el borrador deja de valer y se enseña la cantidad real. Se vacían
  // al cambiar de socio.
  const [borradores, setBorradores] = useState({});
  const teclearPeso = useCallback((clave, campo, txt, qty) => {
    const k = `${clave}|${campo}`;
    const n = Number(String(txt).replace(",", "."));
    const canonico = Number.isFinite(n) && n > 0 && String(n) === String(txt).replace(",", ".");
    setBorradores((prev) => {
      if (canonico) { if (!(k in prev)) return prev; const { [k]: _, ...resto } = prev; return resto; }
      return { ...prev, [k]: { txt, qty } };
    });
  }, []);
  const borradorDe = useCallback((clave, campo, qty) => {
    const b = borradores[`${clave}|${campo}`];
    return b && Number(b.qty) === Number(qty) ? b.txt : undefined;
  }, [borradores]);
  // La caja de peso del dispensario, por tarjeta: Peso ↔ Euro (excluyentes: escribir
  // en uno deriva el otro por el precio) + Báscula opcional (peso real → merma).
  const [pesoDraft, setPesoDraft] = useState({});
  const [avisoMerma, setAvisoMerma] = useState({});
  // Tolerancia de báscula del club (Configuración; default 0,10 g).
  const toleranciaG = useMermaTolerancia();

  const escribirPeso = useCallback((id, campo, valor) => {
    const limpio = valor.replace(",", ".").replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1").slice(0, 8);
    setPesoDraft((d) => {
      const fila = { ...(d[id] || {}), [campo]: limpio };
      // Peso y Euro son excluyentes (misma regla que el dispensario): escribir en
      // uno vacía el otro, que pasa a mostrarse derivado.
      if (campo === "qty") fila.cost = "";
      if (campo === "cost") fila.qty = "";
      return { ...d, [id]: fila };
    });
  }, []);

  const avisarMerma = useCallback((id, msg) => {
    setAvisoMerma((a) => ({ ...a, [id]: msg }));
    setTimeout(() => setAvisoMerma((a) => ({ ...a, [id]: undefined })), 4000);
  }, []);
  // Congelar el carrito SOLO mientras se manda la comanda: un toque a media
  // conversión cambia la versión y la revienta (review). Los guardados normales
  // ya no bloquean nada, que era el bug original.
  const [enviando, setEnviando] = useState(false);
  const versionRef = useRef(0);
  // El bloque del carrito, para que «Ver carrito» de la barra inferior llegue hasta él.
  const carritoRef = useRef(null);
  versionRef.current = cart.version;
  // El carrito responde al TOQUE, no a la red (reporte de Kong 16-ago: "tienes
  // que darle 2 veces para que sume 1"). Antes cada toque era una petición y los
  // botones se deshabilitaban mientras guardaba, así que el toque que caía en esa
  // ventana se perdía. Ahora: se pinta al momento en local y se guarda con un
  // pequeño retardo, agrupando los toques seguidos en una sola escritura.
  const SAVE_DELAY_MS = 350;
  const itemsRef = useRef([]);          // lo último que ve el usuario
  const saveTimerRef = useRef(null);
  const pendienteRef = useRef(null);    // {ownerId, items} a la espera de guardarse
  const enVueloRef = useRef(false);     // hay un guardado en curso
  const enVueloPromesaRef = useRef(null); // …y su promesa, para poder esperarla
  const guardarRef = useRef(null);      // se rellena abajo (saveCart aún no existe)
  const cancelarGuardado = useCallback(() => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
  }, []);
  // Vuelca YA lo pendiente. Se usa antes de cambiar de socio, de enviar la comanda
  // y al desmontar: cancelar sin más perdía lo tocado en los últimos 350 ms.
  const volcarGuardado = useCallback(async () => {
    cancelarGuardado();
    const p = pendienteRef.current;
    if (!p) return;
    pendienteRef.current = null;
    if (guardarRef.current) {
      enVueloPromesaRef.current = guardarRef.current(p.items, { ownerId: p.ownerId });
      await enVueloPromesaRef.current;
    }
  }, [cancelarGuardado]);
  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  const loadCart = useCallback(
    async (socioId) => {
      if (!socioId) return;
      const res = await apiFetch(
        `/v1/cart?venue_id=${venueID}&owner_user_id=${socioId}&context=${CONTEXT}`,
        { headers: authHeaders }
      );
      if (!res.ok) return null;
      const data = await safeJson(res);
      setCart({ items: data?.items || [], version: data?.version || 0 });
      itemsRef.current = data?.items || [];
      return data;
    },
    [venueID, authHeaders]
  );

  useEffect(() => {
    // Volcar (no cancelar): lo tocado en los últimos 350 ms antes de cambiar de
    // socio se guardaba en pantalla pero nunca en el servidor (review). El
    // pendiente lleva su propio ownerId, así que aterriza en el socio correcto.
    volcarGuardado();
    itemsRef.current = [];
    setCart({ items: [], version: 0 });
    setNotaAbierta(null);
    setBorradores({});
    if (atendiendo?.id) loadCart(atendiendo.id);
  }, [atendiendo?.id, loadCart, volcarGuardado]);

  // Solo la versión del carrito del servidor: resuelve un CAS sin repintar lo
  // que el staff está tocando.
  const fetchVersion = useCallback(
    async (socioId) => {
      try {
        const res = await apiFetch(
          `/v1/cart?venue_id=${venueID}&owner_user_id=${socioId}&context=${CONTEXT}`,
          { headers: authHeaders }
        );
        if (!res.ok) return 0;
        const data = await safeJson(res);
        return data?.version || 0;
      } catch {
        return 0;
      }
    },
    [venueID, authHeaders]
  );

  const saveCart = useCallback(
    async (nextItems, { retry = true, baseVersion, ownerId } = {}) => {
      // El dueño viaja explícito: un guardado pendiente puede resolverse cuando
      // el staff YA cambió de socio, y debe seguir yendo al socio correcto.
      const owner = ownerId || atendiendo?.id;
      if (!owner) return;
      enVueloRef.current = true;
      setCartBusy(true);
      try {
        const res = await apiFetch(`/v1/cart`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            venue_id: venueID,
            owner_user_id: owner,
            context: CONTEXT,
            base_version: baseVersion != null ? baseVersion : versionRef.current,
            table_id: mesaId || null,
            items: nextItems.map(lineaParaElServidor),
          }),
        });
        if (res.status === 409 && retry) {
          // CAS: otra pantalla ganó — recargar y reintentar UNA vez sobre lo fresco.
          // La versión se toma de la respuesta, no de versionRef: la ref se
          // actualiza al renderizar y aquí seguimos dentro del mismo await.
          // Conflicto de versión: se pide la versión fresca y se reenvía lo que
          // el staff tiene delante. Recargar el carrito entero aquí pisaba sus
          // cantidades y perdía el toque en silencio.
          const fresh = await fetchVersion(owner);
          return saveCart(itemsRef.current, { retry: false, baseVersion: fresh, ownerId: owner });
        }
        if (!res.ok) {
          const detail = (await res.json().catch(() => null))?.detail;
          throw new Error(typeof detail === "string" ? detail : "No se pudo guardar el carrito");
        }
        const data = await safeJson(res);
        // MIENTRAS SE ATIENDE, LA PANTALLA MANDA. De la respuesta solo se toma la
        // versión (para el CAS); las líneas NO se reescriben. Aplicarlas era la
        // causa del baile de números del vídeo (17-ago): con varios guardados en
        // vuelo, una respuesta vieja repintaba una cantidad anterior y el número
        // subía y bajaba solo. El servidor se relee al entrar, al cambiar de
        // socio y tras enviar la comanda, que es cuando toca.
        setCart((c) => ({ ...c, version: data?.version || 0 }));
        setMsg(null);
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      } finally {
        enVueloRef.current = false;
        setCartBusy(false);
        // ¿Se tocó algo mientras guardábamos? Se guarda ahora, en serie: dos
        // escrituras a la vez compartían base_version y se auto-provocaban un
        // 409 (review).
        const p = pendienteRef.current;
        if (p) {
          pendienteRef.current = null;
          enVueloPromesaRef.current = saveCart(p.items, { ownerId: p.ownerId });
        }
      }
    },
    [atendiendo?.id, venueID, authHeaders, fetchVersion, mesaId]
  );
  guardarRef.current = saveCart;

  // Pinta en local y programa UN guardado para la ráfaga de toques.
  const aplicarEnLocal = useCallback(
    (items) => {
      itemsRef.current = items;
      setCart((c) => ({ ...c, items }));
      pendienteRef.current = { ownerId: atendiendo?.id, items };
      cancelarGuardado();
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        if (enVueloRef.current) return;   // al terminar el que hay en curso se guarda
        const p = pendienteRef.current;
        if (!p) return;
        pendienteRef.current = null;
        enVueloPromesaRef.current = saveCart(p.items, { ownerId: p.ownerId });
      }, SAVE_DELAY_MS);
    },
    [saveCart, cancelarGuardado, atendiendo?.id]
  );

  // La línea de coste del socio atendido, IZADA: una sola grafía para todas las
  // resoluciones de precio de esta pantalla, y deps primitivas en los callbacks
  // (convención del fichero: saveCart depende de atendiendo?.id, no del objeto).
  const lineaCoste = atendiendo?.profile?.cost_line;
  const precioDe = useCallback((p) => effectiveUnitPrice(p, lineaCoste), [lineaCoste]);

  const addProduct = useCallback(

    (p, cuantas, realWeight) => {
      // Por unidades, el campo vacío significa "una": es el gesto rápido de la
      // barra. Por peso NO hay cantidad por defecto —un gramo no es una ración—,
      // así que sin gramos tecleados no se añade nada; el botón ya sale apagado.
      // Peso con 3 decimales (3,5 g de flor); unidades enteras como siempre.
      const n = esPeso(p)
        ? Number((Number(cuantas) || 0).toFixed(3))
        : Math.max(1, Math.round(Number(cuantas) || 0));
      if (n <= 0) return;
      const items = [...itemsRef.current];
      const idx = items.findIndex((it) => it.product_id === p.id && !it.location_id);
      if (idx >= 0) {
        // La báscula se SUMA al fusionar solo si AMBOS añadidos venían pesados
        // (dos cortes pesados por separado). Si uno vino sin báscula, la línea
        // fusionada queda SIN báscula y se pesa al servir: sumar tratando el
        // hueco como 0 dejaba quantity=12/real=2.01 — una comanda incobrable o,
        // peor, 12 g cobrados y 2 g descontados (hallazgo code review 23-08).
        const previa = items[idx].real_weight;
        const real = (previa != null && realWeight != null)
          ? Number((Number(previa) + Number(realWeight)).toFixed(3))
          : undefined;
        items[idx] = { ...items[idx], quantity: Number(items[idx].quantity) + n, real_weight: real };
      }
      // `_unit_type` viaja con la línea igual que `_name`: así la línea sabe leerse
      // sola, sin depender de que el catálogo esté cargado (ver `unitTypeDeLinea`).
      // El precio que se congela es el de la LÍNEA DE COSTE del socio atendido —
      // el mismo que el cobro aplicará al servir. Con `p.price` a pelo, el ticket
      // cantaba la tarifa y el monedero se movía con la línea (CRUO, 03-09-2026).
      else items.push({ product_id: p.id, quantity: n, unit_price: precioDe(p), real_weight: realWeight ?? undefined, _name: p.name, _unit_type: p.unit_type });
      aplicarEnLocal(items);
      setAddQty((q) => ({ ...q, [p.id]: "" }));   // el campo se vacía tras añadir
    },
    [aplicarEnLocal, precioDe]
  );

  // El «+ Añadir» de una tarjeta a peso: resuelve gramos (de Peso o de Euro/precio),
  // valida la merma si hay báscula (el backend re-valida al cobrar: el FE avisa,
  // el backend manda) y limpia la caja. Báscula vacía = válido, se pesa al servir.
  const anadirPeso = useCallback(
    (p) => {
      const d = pesoDraft[p.id] || {};
      const precio = precioDe(p);
      const gramos = d.qty
        ? parseFloat(d.qty)
        : d.cost && precio > 0
          ? parseFloat(d.cost) / precio
          : 0;
      const finalQty = Number((gramos || 0).toFixed(3));
      if (!(finalQty > 0)) return;
      const realW = parseFloat(d.bascula);
      const hayBascula = !Number.isNaN(realW) && realW > 0;
      const merma = hayBascula ? mermaDe(finalQty, realW) : null;
      if (hayBascula && Math.abs(merma) > toleranciaG) {
        avisarMerma(
          p.id,
          `Merma fuera de rango: ${merma > 0 ? "+" : ""}${merma.toFixed(3)} g (máx ±${toleranciaG.toFixed(2)} g)`
        );
        return;
      }
      addProduct(p, finalQty, hayBascula ? Number(realW.toFixed(3)) : undefined);
      setPesoDraft((s2) => ({ ...s2, [p.id]: { qty: "", cost: "", bascula: "" } }));
    },
    [pesoDraft, toleranciaG, addProduct, avisarMerma, precioDe]
  );

  const setQty = useCallback(
    (productId, qty) => {
      const items = itemsRef.current
        // Editar la cantidad INVALIDA la báscula anterior (se pesó otro corte):
        // conservarla dejaba quantity=3.5/real=5.02 → comanda bloqueada en Barra
        // o stock mal descontado (hallazgo code review 23-08). La línea queda
        // sin báscula y se pesa al servir.
        .map((it) => (it.product_id === productId ? { ...it, quantity: qty, real_weight: undefined } : it))
        .filter((it) => Number(it.quantity) > 0);
      aplicarEnLocal(items);
    },
    [aplicarEnLocal]
  );

  // La nota viaja EN la línea, como la cantidad: se pinta al momento y se guarda con
  // el mismo retardo agrupado, así escribir no dispara una petición por tecla. Cortar
  // a NOTA_MAX aquí (y no solo con `maxLength`) tapa el pegado y el dictado, que se
  // saltan el atributo y llegarían al servidor con más de 200.
  const setNota = useCallback(
    (it, texto) => {
      const limpio = String(texto ?? "").slice(0, NOTA_MAX);
      const clave = lineaKey(it);
      const items = itemsRef.current.map((linea) =>
        lineaKey(linea) === clave ? { ...linea, notes: limpio || null } : linea
      );
      aplicarEnLocal(items);
    },
    [aplicarEnLocal]
  );

  const setDescuento = useCallback(
    (it, valor) => {
      // Se guarda crudo mientras se teclea (para poder borrar el campo y dejarlo vacío)
      // y se acota al confirmar. Un 150 tecleado por error se queda en 100, no en un
      // total negativo.
      const crudo = String(valor ?? "").trim();
      const pct = crudo === "" ? null : Math.min(100, Math.max(0, Number(crudo) || 0));
      const clave = lineaKey(it);
      const items = itemsRef.current.map((linea) =>
        lineaKey(linea) === clave ? { ...linea, discount_pct: pct } : linea
      );
      aplicarEnLocal(items);
    },
    [aplicarEnLocal]
  );

  const productoDe = useCallback(
    (productId) => products.find((p) => p.id === productId) || null,
    [products]
  );

  const nameOf = useCallback(
    (productId) => productoDe(productId)?.name || "Producto",
    [productoDe]
  );

  // El descuento del SOCIO, con la MISMA tasa que aplica el cobro. La comanda se
  // cobra vía make_purchase SIN cart_type, así que el 20% terapéutico SÍ aplica
  // — la Sala pintaba «modo productos» (solo % de ficha) y el monedero se
  // llevaba el 30%: pantalla 2,25 €, cobro 1,75 €. Decidido el 03-09-2026: la
  // pantalla dice lo que se cobra (el cobro no cambia ni un céntimo).
  const dtoSocio = resolveMemberDiscount(atendiendo?.profile, false);

  // Subtotal, descuento y total: la MISMA fórmula que el POS (lib/cartTotals),
  // que encadena manual → socio por línea y redondea a céntimos. Esta pantalla
  // tenía su copia a mano — la tercera — y así fue como el precio por línea se
  // quedó fuera en su día. Un dato, un render.
  // Traducción en la frontera: la fórmula compartida habla {price, quantity,
  // manual_discount}; las líneas de Sala hablan {unit_price, discount_pct}.
  const { subtotal, descuento, total } = calcularTotalesCarrito(
    cart.items.map((it) => ({ price: it.unit_price, quantity: it.quantity, manual_discount: it.discount_pct })),
    dtoSocio.rate,
  );
  const saldo = Number(atendiendo?.credit_balance ?? atendiendo?.profile?.credit_balance ?? 0);

  // ── enviar a barra ────────────────────────────────────────────────────────
  const enviarABarra = useCallback(async () => {
    if (!atendiendo?.id || cart.items.length === 0 || cartBusy || enviando) return;
    cancelarGuardado();          // el POST de aquí abajo ya manda el carrito local
    pendienteRef.current = null;
    setEnviando(true);
    // Si había un guardado en vuelo, se espera: dos escrituras a la vez sobre el
    // mismo carrito chocan de versión y la comanda fallaría al convertirse.
    try { await enVueloPromesaRef.current; } catch { /* su propio error ya se pintó */ }
    setCartBusy(true);
    try {
      // Fijar la mesa en el carrito del servidor justo antes de convertir
      // (el conversor deriva serve/pickup de cart.table_id).
      await apiFetch(`/v1/cart`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          venue_id: venueID,
          owner_user_id: atendiendo.id,
          context: CONTEXT,
          base_version: versionRef.current,
          table_id: mesaId || null,
          items: cart.items.map(lineaParaElServidor),
        }),
      }).then(async (r) => {
        if (r.ok) {
          const d = await safeJson(r);
          setCart({ items: d?.items || [], version: d?.version || 0 });
          itemsRef.current = d?.items || [];
          versionRef.current = d?.version || 0;
        }
      });
      const res = await apiFetch(`/v1/cart/checkout-to-order`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          venue_id: venueID,
          owner_user_id: atendiendo.id,
          context: CONTEXT,
          base_version: versionRef.current,
        }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail;
        throw new Error(typeof detail === "string" ? detail : "No se pudo enviar la comanda");
      }
      const order = await safeJson(res);
      itemsRef.current = [];
      setCart({ items: [], version: 0 });
      setMesaId("");
      setMsg({
        tone: "ok",
        text: `Comanda de ${atendiendo?.profile?.first_name || "socio"} enviada a Barra ✓ (${
          order?.fulfillment === "serve" ? "servir en mesa" : "recoge en barra"
        })`,
      });
    } catch (e) {
      setMsg({ tone: "err", text: e.message });
    } finally {
      setEnviando(false);
      setCartBusy(false);
    }
  }, [atendiendo, cart.items, cartBusy, enviando, venueID, authHeaders, mesaId, cancelarGuardado]);

  // ── entrada / salida ──────────────────────────────────────────────────────
  const [saldoUserId, setSaldoUserId] = useState(null); // socio del modal "+ Saldo"
  const [vinculo, setVinculo] = useState(null); // {socio, url} → modal QR de vinculación
  const [showNueva, setShowNueva] = useState(false);
  // Ficha del socio: se abre tocando su nombre en la lista de sala. Estaba en
  // Dispensario y en Socios pero no aquí, que es justo donde atiende un club sin
  // pantalla de Dispensario (CRUO) — reporte del 25-ago.
  const [fichaSocio, setFichaSocio] = useState(null);
  const [nuevaQ, setNuevaQ] = useState("");
  const [nuevaResults, setNuevaResults] = useState([]);
  const [nuevaBuscando, setNuevaBuscando] = useState(false);

  useEffect(() => {
    if (!showNueva || !busquedaSuficiente(nuevaQ)) {
      setNuevaResults([]);
      return undefined;
    }
    setNuevaBuscando(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiFetch(
          `/v1/clients/search/${venueID}?q=${encodeURIComponent(nuevaQ.trim())}&limit=20`,
          { headers: authHeaders }
        );
        if (res.ok) setNuevaResults((await safeJson(res)) || []);
      } catch {
        /* búsqueda silenciosa */
      } finally {
        setNuevaBuscando(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [showNueva, nuevaQ, venueID, authHeaders]);

  const registrarEntrada = useCallback(
    async (client) => {
      try {
        const res = await apiFetch(`/v1/entries`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ user_id: client.id, venue_id: venueID }),
        });
        if (!res.ok) throw new Error("No se pudo registrar la entrada");
        markUserEntered({ ...client, entry_time: new Date() });
        if (tieneAvisoSocio(client)) setAvisoSocio(client);
        setShowNueva(false);
        setNuevaQ("");
        refreshSala?.();
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, venueID, markUserEntered, refreshSala]
  );

  const vincularMovil = useCallback(
    async (socio) => {
      try {
        const res = await apiFetch(`/v1/member/link-session`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ venue_id: venueID, member_user_id: socio.id }),
        });
        // Si el servidor da motivo (socio de otro club, sin permiso…) se enseña;
        // el texto fijo solo queda de último recurso.
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(apiErrorDetail(err, "No se pudo generar el QR de vinculación"));
        }
        const data = await safeJson(res);
        // El QR usa SIEMPRE el origen desde el que el staff abrió la Sala: si la
        // pantalla está en http://192.168.x.x:3001, el móvil del socio (misma wifi)
        // podrá llegar. El FRONTEND_URL del backend (localhost en dev) no sirve aquí.
        const url = `${window.location.origin}/app/vincular?token=${encodeURIComponent(data.link_token)}`;
        setVinculo({ socio, url, min: data.expires_in_minutes });
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, venueID]
  );

  const registrarSalida = useCallback(
    async (socio) => {
      try {
        const res = await apiFetch(`/v1/checkout?user_id=${socio.id}`, {
          method: "PATCH",
          headers: authHeaders,
        });
        if (!res.ok) throw new Error("No se pudo registrar la salida");
        markUserLeft(socio.id);
        if (String(atendiendo?.id) === String(socio.id)) setAtendiendo(null);
      } catch (e) {
        setMsg({ tone: "err", text: e.message });
      }
    },
    [authHeaders, markUserLeft, atendiendo?.id]
  );

  // ── render ────────────────────────────────────────────────────────────────
  // Por debajo de lg el carrito es el último bloque de la columna: se añadía y la
  // pantalla no cambiaba (auditoría CRUO 21-09, CRUO-004). La misma barra inferior
  // que el Dispensario (CartBar, Figma Club_CartBar) enseña el total y lleva al carrito.
  const barraCarrito = Boolean(atendiendo) && cart.items.length > 0;
  return (
    // `overflow-x-clip` y no `overflow-x-hidden`: hidden convertía esta raíz en su
    // propio scroller (overflow-y pasa a auto) y `main` dejaba de scrollear, con lo
    // que ni el hueco que main reserva a la barra inferior ni el ocultar la
    // navegación móvil al bajar le llegaban a la Sala (CRUO-004).
    // Con la barra puesta, la raíz reserva su hueco: por debajo de md el de la barra
    // MÁS el de la navegación móvil (Layout retira el suyo cuando la esconde al
    // bajar, y la barra se quedaría sobre «Vaciar carrito» al final del scroll).
    <div className={`flex max-w-full flex-col gap-4 overflow-x-clip p-4${barraCarrito ? " pb-32 md:pb-24 lg:pb-4" : ""}`}>
      {/* ── Caja 1: la SALA (jerarquía del diseño cannabis: cajas blancas sobre fondo gris) ── */}
      <section className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-[var(--foreground)]">
          Sala{" "}
          <span className="ml-1 rounded-full bg-[var(--muted)] px-2.5 py-0.5 text-sm font-semibold text-[var(--muted-foreground)]">
            {usersInVenue.length} dentro
          </span>
        </h1>
        {/* Envuelve: a 390 el buscador ocupa la fila y «Nueva entrada» baja entera
            (Button no parte su texto; el <button> a mano lo partía en dos líneas). */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input
            value={salaSearch}
            onChange={(e) => setSalaSearch(e.target.value)}
            placeholder="Buscar socio…"
            className="h-10 w-full rounded-md border border-[var(--border)] px-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)] sm:w-56"
          />
          <Button size="lg" onClick={() => setShowNueva(true)}>
            Nueva entrada
          </Button>
        </div>
      </div>

      {msg && (
        <div
          role="status"
          className={`mb-3 rounded-lg border px-4 py-2 text-sm ${
            msg.tone === "ok"
              ? "border-[var(--toast-success-border)] bg-[var(--toast-success-bg)] text-[var(--toast-success-text)]"
              : "border-[var(--toast-error-border)] bg-[var(--toast-error-bg)] text-[var(--toast-error-text)]"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {salaFiltrada.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-[var(--border)] p-8 text-center text-[var(--muted-foreground)]">
            No hay socios en sala.
          </div>
        )}
        {salaFiltrada.map((u) => {
          const activo = String(atendiendo?.id) === String(u.id);
          const cred = Number(u?.credit_balance ?? u?.profile?.credit_balance ?? 0);
          return (
            <div
              key={u.id}
              /* Sin sombra y con hover por trazo (regla de Pablo); la tarjeta del socio
                 atendido se marca con el anillo de selección del sistema, no con verde:
                 verde es éxito («En sala»), no «seleccionado». */
              className={`rounded-lg border bg-[var(--card)] p-3 transition-colors ${
                activo ? "border-[var(--primary)] ring-1 ring-[var(--ring-selected)]" : "border-[var(--border)] hover:border-[var(--border-hover)]"
              }`}
            >
              {/* Fila 1: foto + nombre + saldo/tiempo. Fila 2: botones (con wrap).
                  Apilado a propósito: no puede desbordarse a ningún ancho. */}
              <div className="flex items-center gap-3">
                <MemberPhoto
                  path={u?.profile?.image_path || u?.profile?.documents_path}
                  name={u?.profile?.first_name || u?.email || ""}
                  className="h-11 w-11 flex-shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {/* Entero, como el del producto: «no como más arriba en socios en
                        sala» (reporte 25-08). La fila ya envuelve, así que un nombre largo
                        baja de línea en vez de comerse los chips. */}
                    <button
                      type="button"
                      onClick={() => setFichaSocio(u)}
                      title="Abrir la ficha del socio"
                      className="min-w-0 break-words rounded text-left text-sm font-semibold text-[var(--foreground)] underline decoration-transparent underline-offset-2 transition-colors hover:decoration-[var(--muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                    >
                      {`${u?.profile?.first_name || ""} ${u?.profile?.last_name || ""}`.trim() ||
                        u?.email ||
                        "Socio"}
                    </button>
                    <EnSalaChip />
                    <CaducadoChip socio={u} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <NumSocioChip socio={u} />
                    {/* La misma pareja fondo+texto que el banner y que Socios/Entradas
                        (helper `balanceChipClass`): antes esta tarjeta tenía su propio
                        «rojo si < 0» y el banner el del sistema, dos verdades. */}
                    <span className={`rounded px-2 py-0.5 font-medium ${balanceChipClass(cred)}`}>
                      Crédito: <b>{eur(cred)}</b>
                    </span>
                    <span className="text-[var(--muted-foreground)]">
                      {u?.entry_time ? <EntryTimer entryTime={u.entry_time} /> : "—"}
                    </span>
                  </div>
                </div>
              </div>
              {/* Los botones del sistema en su tamaño de fila (Button sm, 32 px): antes cinco
                  <button> a mano de 30 px con su propio color cada uno (CRUO-006). El
                  tamaño pequeño, con 4 px de aire, es lo que cabe en UNA fila con la
                  tarjeta a tres columnas (1280): con 6 px «Salida» se iba a otra línea. */}
              <div className="mt-2.5 flex flex-wrap gap-1">
                {activo ? (
                  <Button variant="outline" size="sm" onClick={() => setAtendiendo(null)}>
                    Cerrar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => {
                      setAtendiendo(u);
                      if (notaConAviso(u)) setAvisoSocio(u);
                    }}
                  >
                    Atender
                  </Button>
                )}
                {/* «Saldo», como en la tarjeta de Entradas (no «+ Saldo»). */}
                <Button variant="outline" size="sm" onClick={() => setSaldoUserId(u.id)}>
                  Saldo
                </Button>
                {/* Solo icono: 32×32. El `!` hace falta porque el `px-3` del tamaño se
                    genera después en el CSS de Tailwind y pisa un `px-2` normal. */}
                <Button
                  variant="outline"
                  size="sm"
                  className="!px-2"
                  onClick={() => vincularMovil(u)}
                  aria-label={`Vincular el móvil de ${u?.profile?.first_name || u?.email || "socio"}`}
                  title="Vincular el móvil del socio (QR de acceso a la app)"
                >
                  <Smartphone size={16} strokeWidth={1.5} aria-hidden />
                </Button>
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => registrarSalida(u)}>
                  Salida
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      </section>

      {/* ── Hero "¿A quién atendemos?" cuando no hay socio (igual que cannabis) ── */}
      {!atendiendo && (
        <section className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-[var(--foreground)]">¿A quién atendemos?</h2>
          <p className="mt-1 text-[var(--muted-foreground)]">Busca un socio en sala para añadir productos.</p>
        </section>
      )}

      {/* Atención: productos + carrito */}
      {atendiendo && (
        <>
        {/* ── Banner del socio atendido: el mismo anillo de selección que su tarjeta ── */}
        <section className="rounded-lg border border-[var(--primary)] bg-[var(--card)] p-3 ring-1 ring-[var(--ring-selected)]">
          <div className="flex flex-wrap items-center gap-3">
            <MemberPhoto
              path={atendiendo?.profile?.image_path || atendiendo?.profile?.documents_path}
              name={atendiendo?.profile?.first_name || atendiendo?.email || ""}
              className="h-[46px] w-[46px] flex-shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="min-w-0 break-words text-base font-semibold text-[var(--foreground)]">
                  {`${atendiendo?.profile?.first_name || ""} ${atendiendo?.profile?.last_name || ""}`.trim() || atendiendo?.email || "Socio"}
                </span>
                <EnSalaChip />
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                <NumSocioChip socio={atendiendo} />
                {/* El cero no se enrojecía aquí y sí en Socios y en Entradas: el mismo
                    socio a 0,00 € cambiaba de color según la pantalla. La pareja
                    fondo+texto sale del helper para que no vuelva a divergir. */}
                <span className={`rounded px-2 py-0.5 font-medium ${balanceChipClass(saldo)}`}>
                  Crédito: <b>{eur(saldo)}</b>
                </span>
              </div>
            </div>
            <Button variant="outline" className="ml-auto" onClick={() => setAtendiendo(null)}>
              <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
              Cambiar socio
            </Button>
          </div>
        </section>

        {/* ≥ lg: rejilla flexible + carrito de 340 px (ClubEntradas.css). */}
        <div className="sala-fila grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="min-w-0 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4">
            {/* Las mismas píldoras que Almacén (PildorasCategoria): activa en primario, resto outline. */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {categorias.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoria(c)}
                  aria-pressed={categoria === c}
                  className={`${PILDORA} ${categoria === c ? PILDORA_ACTIVA : PILDORA_INACTIVA}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative mb-4">
              <input
                value={prodSearch}
                onChange={(e) => setProdSearch(e.target.value)}
                placeholder="Buscar producto..."
                className="h-11 w-full rounded-lg border border-[var(--border)] bg-white px-4 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--foreground)]"
              />
            </div>
            <div className="sala-rejilla grid grid-cols-2 gap-3 sm:grid-cols-3">
              {productosVisibles.map((p) => (
                <div
                  key={p.id}
                  /* La misma card de producto que el dispensario y la barra, así que
                     el mismo radio (`radius-lg`) y el mismo trazo marcando el estado:
                     no debería medir distinto según la sección. */
                  className="flex flex-col rounded-lg border border-[var(--border)] bg-white p-1.5 text-xs transition-colors hover:border-[var(--primary)]"
                >
                  <div className="relative w-full overflow-hidden rounded-lg bg-[var(--muted)]" style={{ paddingTop: "100%" }}>
                    <img
                      src={(p.image_path ? getImageUrl(p.image_path) : null) || notFoundImage}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="absolute left-0 top-0 h-full w-full object-cover"
                      onError={(e) => {
                        e.target.src = notFoundImage;
                      }}
                    />
                  </div>
                  <span className="mt-1 inline-flex w-fit rounded bg-[var(--muted)] px-2 py-0.5 text-[10px] font-medium text-[var(--muted-foreground)]">
                    {p?.category?.name || "Sin categoría"}
                  </span>
                  {/* El nombre ENTERO, sin «…» (petición CRUO 24-08). Se recortaba a una
                      línea y dos productos distintos se veían idénticos —«E2E LIVE Cola 1730»
                      y «…1815»—, así que el camarero creía que la línea estaba duplicada.
                      `break-words` parte los nombres largos de una pieza en vez de desbordar. */}
                  <h3 className="mt-0.5 break-words text-sm font-medium leading-tight text-[var(--foreground)]">{p.name}</h3>
                  <p className="mb-1 text-[11px] text-[var(--muted-foreground)]">
                    {precioDe(p).toFixed(2)} €/{unidadDe(p)}
                  </p>
                  {esPeso(p) ? (
                    /* La caja del dispensario, en la Sala («el usuario tiene que ver lo
                       mismo que en dispensario», CRUO 23-08): Peso ↔ Euro enlazados por
                       el precio y Báscula opcional. La báscula viaja con la línea y llega
                       pre-cargada a «Pesar antes de servir» en la Barra. */
                    (() => {
                      const d = pesoDraft[p.id] || {};
                      const precio = precioDe(p);
                      const qtyMostrado = d.cost && precio > 0 ? (parseFloat(d.cost) / precio).toFixed(3) : (d.qty ?? "");
                      const costMostrado = d.qty ? (parseFloat(d.qty) * precio).toFixed(2) : (d.cost ?? "");
                      const listo = Number(qtyMostrado) > 0;
                      return (
                        <div className="mt-auto flex flex-col gap-1">
                          <div className="grid grid-cols-2 gap-1">
                            <label className="flex flex-col gap-0.5 text-[10px] font-medium text-[var(--muted-foreground)]">
                              Peso
                              <input
                                type="text"
                                inputMode="decimal"
                                aria-label={`Gramos de ${p.name}`}
                                value={qtyMostrado}
                                onChange={(e) => escribirPeso(p.id, "qty", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); anadirPeso(p); } }}
                                placeholder="g"
                                disabled={enviando}
                                className="h-7 w-full rounded-md border border-[var(--border)] bg-white px-1 text-center text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] disabled:bg-[var(--muted)]"
                              />
                            </label>
                            <label className="flex flex-col gap-0.5 text-[10px] font-medium text-[var(--muted-foreground)]">
                              Euro
                              <input
                                type="text"
                                inputMode="decimal"
                                aria-label={`Euros de ${p.name}`}
                                value={costMostrado}
                                onChange={(e) => escribirPeso(p.id, "cost", e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); anadirPeso(p); } }}
                                placeholder="€"
                                disabled={enviando}
                                className="h-7 w-full rounded-md border border-[var(--border)] bg-white px-1 text-center text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] disabled:bg-[var(--muted)]"
                              />
                            </label>
                          </div>
                          <label className="flex flex-col gap-0.5 text-[10px] font-medium text-[var(--muted-foreground)]">
                            Báscula
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`Báscula de ${p.name}`}
                              value={d.bascula ?? ""}
                              onChange={(e) => escribirPeso(p.id, "bascula", e.target.value)}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); anadirPeso(p); } }}
                              placeholder="real (opcional)"
                              disabled={enviando}
                              className="h-7 w-full rounded-md border border-[var(--border)] bg-white px-1 text-center text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] disabled:bg-[var(--muted)]"
                            />
                          </label>
                          {avisoMerma[p.id] && (
                            <p className="text-[10px] font-medium text-[var(--destructive)]">{avisoMerma[p.id]}</p>
                          )}
                          <Button
                            size="sm"
                            className="w-full touch-manipulation active:scale-95"
                            aria-label={`+ Añadir ${p.name}`}
                            disabled={enviando || !listo}
                            onClick={() => anadirPeso(p)}
                          >
                            + Añadir
                          </Button>
                        </div>
                      );
                    })()
                  ) : (
                  <div className="mt-auto flex items-center gap-1">
                    <div className="relative shrink-0">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        aria-label={`Cantidad de ${p.name}`}
                        value={addQty[p.id] ?? ""}
                        onChange={(e) =>
                          setAddQty((q) => ({ ...q, [p.id]: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))
                        }
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProduct(p, addQty[p.id]); } }}
                        placeholder="1"
                        disabled={enviando}
                        className="h-7 w-9 rounded-md border border-[var(--border)] bg-white px-1 text-center text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] disabled:bg-[var(--muted)]"
                      />
                    </div>
                    <Button
                      size="sm"
                      // Una rejilla de botones que se llaman todos "+ Añadir" no se
                      // puede usar a ciegas; el nombre lleva el producto.
                      aria-label={`+ Añadir ${p.name}`}
                      className="min-w-0 flex-1 touch-manipulation active:scale-95"
                      disabled={enviando}
                      onClick={() => addProduct(p, addQty[p.id])}
                    >
                      + Añadir
                    </Button>
                  </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div ref={carritoRef} className="scroll-mt-4 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 lg:sticky lg:top-4 lg:self-start">
            <h3 className="text-lg font-semibold text-[var(--foreground)]">Carrito</h3>
            <div className="mb-3 mt-0.5 truncate text-xs text-[var(--muted-foreground)]">
              {`${atendiendo?.profile?.first_name || ""} ${atendiendo?.profile?.last_name || ""}`.trim() || "Socio"}
              {socioNum(atendiendo) != null && ` · Nº Socio: ${socioNum(atendiendo)}`}
            </div>

            {cart.items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--muted-foreground)]">
                Toca productos para añadirlos.
              </div>
            ) : (
              <>
              {/* Las líneas son `Elements/CartRow`, la fila del carrito de Pablo
                  (Figma Club_cart row), la misma del POS: miniatura, nombre, precio
                  unitario, cantidad, importe y ✕ alineados por rejilla, separadas por
                  el borde del sistema y con «pedido · merma» bajo el precio cuando hay
                  báscula. Antes era una lista a mano con cabecera «PRODUCTO / CANTIDAD ·
                  PRECIO» que se descuadraba (Yago, 20-09-2026). Lo propio de la Sala —Dto.
                  por línea y la nota para cocina— va en el hueco `extra`. */}
              <ul className="divide-y divide-[var(--border)]">
                {cart.items.map((it) => {
                  const nombre = it._name || nameOf(it.product_id);
                  const producto = productoDe(it.product_id);
                  const porPeso = esPeso({ unit_type: unitTypeDeLinea(it, producto) });
                  const clave = lineaKey(it);
                  // Con nota escrita el campo se ve SIEMPRE: la cocina la lee de un
                  // vistazo y se corrige tecleando encima, sin abrir nada.
                  const notaVisible = notaAbierta === clave || !!it.notes;
                  const fila = {
                    id: it.product_id,
                    name: nombre,
                    price: Number(it.unit_price || 0),
                    quantity: Number(it.quantity),
                    real_weight: it.real_weight,
                    image_path: producto?.image_path,
                    category: producto?.category,
                    // Lo tecleado a medias («2.», «2.50») se enseña tal cual mientras
                    // se escribe; el carrito guarda el número en cuanto vale.
                    weight_draft: borradorDe(clave, "g", it.quantity),
                  };
                  const extra = (
                    <div className="mt-1 space-y-1">
                      {/* Descuento de ESTA línea. */}
                      <label className="flex items-center gap-1 text-[11px] text-[var(--muted-foreground)]">
                        <span>Dto.</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          disabled={enviando}
                          value={it.discount_pct ?? ""}
                          onChange={(e) => setDescuento(it, e.target.value)}
                          placeholder="0"
                          aria-label={`Descuento de ${nombre} en porcentaje`}
                          className="w-12 rounded-sm border border-[var(--border)] px-1 py-0.5 text-right tabular-nums focus:border-[var(--primary)] focus:outline-none"
                        />
                        <span>%</span>
                      </label>

                      {/* Nota para la cocina: se escribe de pie y con una mano, así que
                          es un campo a la vista —sin modal, sin pasos—. Un toque en
                          "+ nota" abre el campo YA enfocado (el teclado de la tablet sube
                          solo) y la ✕ la borra. */}
                      {notaVisible ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={it.notes || ""}
                            maxLength={NOTA_MAX}
                            disabled={enviando}
                            autoFocus={notaAbierta === clave}
                            aria-label={`Nota para ${nombre}`}
                            placeholder="sin cebolla, muy hecho…"
                            onChange={(e) => setNota(it, e.target.value)}
                            onKeyDown={(e) => {
                              // Enter cierra y suelta el teclado: la nota ya está
                              // escrita, no hay nada que confirmar.
                              if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); setNotaAbierta(null); }
                            }}
                            className="h-8 min-w-0 flex-1 rounded-sm border border-[var(--border)] bg-[var(--muted)] px-2 text-xs italic text-[var(--foreground)] placeholder:not-italic placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-40"
                          />
                          <button
                            type="button"
                            aria-label={`Borrar nota de ${nombre}`}
                            disabled={enviando}
                            onClick={() => { setNota(it, ""); setNotaAbierta(null); }}
                            className="h-8 w-8 shrink-0 rounded-sm leading-none text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--destructive)] disabled:opacity-40"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Añadir nota a ${nombre}`}
                          disabled={enviando}
                          onClick={() => setNotaAbierta(clave)}
                          className="rounded-sm px-1 py-0.5 text-[11px] font-medium text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] disabled:opacity-40"
                        >
                          + nota
                        </button>
                      )}
                      {/* El aviso solo aparece pegado al tope: explica el corte en vez
                          de comerse las letras en silencio. */}
                      {(it.notes || "").length >= NOTA_MAX - 20 && (
                        <div className="text-right text-[10px] text-[var(--muted-foreground)]">
                          {(it.notes || "").length}/{NOTA_MAX}
                        </div>
                      )}
                    </div>
                  );
                  return (
                    <li key={clave} className="py-1">
                      <CartRow
                        item={fila}
                        medida={porPeso ? "peso" : "unidad"}
                        nombreEntero
                        compacta
                        // «… en el carrito»: la tarjeta del producto ya se llama «Cantidad de X» /
                        // «Gramos de X», y las dos conviven en pantalla.
                        labelCantidad={porPeso ? `Gramos de ${nombre} en el carrito` : `Cantidad de ${nombre} en el carrito`}
                        // Unidades: el contador del sistema (−/+ y teclado). Vacío o 0
                        // no se confirma: para sacar la línea está la ✕.
                        onQuantityChange={(v) => {
                          const n = parseInt(v, 10);
                          if (Number.isFinite(n) && n >= 1) setQty(it.product_id, n);
                        }}
                        // Peso: los gramos se corrigen desde la línea (invalida la báscula,
                        // setQty); el importe varía con ellos pero no se teclea (Yago, 20-09).
                        importeEditable={false}
                        onWeightChange={(txt) => {
                          const g = Number(String(txt).replace(",", "."));
                          const vale = Number.isFinite(g) && g > 0;
                          const qty = vale ? Number(g.toFixed(3)) : it.quantity;
                          teclearPeso(clave, "g", txt, qty);
                          if (vale) setQty(it.product_id, qty);
                        }}
                        onRemove={() => setQty(it.product_id, 0)}
                        disabled={enviando}
                        extra={extra}
                      />
                    </li>
                  );
                })}
              </ul>
              </>
            )}

            {/* El resumen va con el mismo formato de importe que las líneas (CartRow /
                CartFooter: «€6.00»), no con el «6.00 €» de las tarjetas de socio. */}
            <div className="mt-4 space-y-1 border-t border-[var(--border)] pt-3 text-sm">
              <div className="flex items-center justify-between text-[var(--muted-foreground)]">
                <span>Subtotal ({cart.items.length} producto{cart.items.length === 1 ? "" : "s"})</span>
                <span className="tabular-nums">{eurSistema(subtotal)}</span>
              </div>
              {/* El descuento SOLO se enseña si lo hay. Sin esta línea el total bajaba
                  y no había forma de saber por qué — y un total que no se explica es un
                  total que el club no se cree. */}
              {descuento > 0 && (
                <div className="flex items-center justify-between text-[var(--success)]">
                  {/* Con la etiqueta del socio cuando la hay («50%»): el camarero
                      canta el precio, y si no sabe de dónde sale el descuento no
                      puede explicárselo al socio que lo pregunta. */}
                  <span>Descuento{dtoSocio.label ? ` (socio ${dtoSocio.label})` : ""}</span>
                  <span className="tabular-nums">−{eurSistema(descuento)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-[var(--muted-foreground)]">
                <span>Saldo disponible</span>
                <span className={`tabular-nums font-semibold ${balanceToneClass(saldo, "text-[var(--foreground)]")}`}>{eurSistema(saldo)}</span>
              </div>
              <div className="flex items-center justify-between pt-1 text-base font-bold text-[var(--foreground)]">
                <span>Total</span>
                <span className="tabular-nums">{eurSistema(total)}</span>
              </div>
            </div>
            {total > saldo && (
              <div className="mt-2 rounded-lg border border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)] px-3 py-1.5 text-xs text-[var(--toast-warning-text)]">
                Se apunta a su cuenta: quedará en <b>{eurSistema(saldo - total)}</b> (se salda al recargar).
              </div>
            )}
            {mesas.length > 0 && (
              <label className="mt-3 block text-xs text-[var(--muted-foreground)]">
                <span className="mb-1 block font-medium">Entrega</span>
                <select
                  value={mesaId}
                  onChange={(e) => setMesaId(e.target.value)}
                  className="h-9 w-full rounded-md border border-[var(--border)] bg-white px-2 text-sm text-[var(--foreground)]"
                >
                  <option value="">🛎️ Recoge en barra</option>
                  {mesas.map((m) => (
                    <option key={m.id} value={m.id}>
                      🍽️ {m.display_name ? `${m.display_name} (${m.label})` : m.label}
                      {m.zone ? ` · ${m.zone}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button
              size="lg"
              className="mt-3 w-full"
              disabled={enviando || cart.items.length === 0}
              onClick={enviarABarra}
            >
              {enviando ? "Enviando…" : "🍹 Enviar a Barra"}
            </Button>
            {/* Ghost en rojo: el `!` porque el `text-[var(--foreground)]` de ghost se
                genera después en el CSS de Tailwind y pisa el destructivo (code-review). */}
            {cart.items.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 w-full !text-[var(--destructive)]"
                disabled={enviando}
                onClick={() => { cancelarGuardado(); pendienteRef.current = null; aplicarEnLocal([]); }}
              >
                Vaciar carrito
              </Button>
            )}
          </div>
        </div>
        </>
      )}

      {/* Modal recarga de saldo (reutiliza el AddCashModal del POS) */}
      <NotaSocioPopup socio={avisoSocio} onClose={() => setAvisoSocio(null)} />

      {fichaSocio && (
        <ClientDetailModal
          active
          handleModal={() => setFichaSocio(null)}
          client={fichaSocio}
          token={token}
          venueID={venueID}
          // La ficha puede cambiar cost_line o descuento del socio ATENDIDO: sin
          // este refresco la Sala congelaba precios con la línea vieja mientras
          // el cobro re-resuelve con la nueva (verificación 04-09) — el mismo
          // patrón que ya hace onCashAdded con el saldo.
          onClientUpdated={(act) => {
            if (!act) return;
            // La caché COMPARTIDA primero (updateUser del hook, el mismo molde
            // que el saldo): reabrir la ficha lee de esa lista, y sin este
            // parche enseñaba el valor viejo aunque la DB guardara bien
            // (reporte 05-09: cost_line editado y «ni lo refleja la ficha»).
            updateUser(act.id, act);
            setFichaSocio((prev) => (prev && String(prev.id) === String(act.id)
              ? { ...prev, ...act, profile: { ...prev.profile, ...act.profile } }
              : prev));
            if (String(atendiendo?.id) === String(act.id)) {
              setAtendiendo((prev) => (prev ? { ...prev, profile: { ...prev.profile, ...act.profile } } : prev));
            }
          }}
          isClientInVenue
          // Sin esto, el socio se borraba y su fila seguía en «Socios en sala»
          // hasta recargar: la ficha borra por su cuenta (esta pantalla no
          // cablea `onDelete`) y nadie le decía a la lista que ya no está.
          onDeleted={() => refreshSala()}
          onNotesSaved={(clientId, cambios) => {
            // Sin esto, la nota o el aviso que se acaban de guardar mueren en el modal:
            // la lista de sala seguiría con el flag viejo hasta recargar.
            setFichaSocio((prev) =>
              prev && String(prev.id) === String(clientId)
                ? { ...prev, profile: { ...(prev.profile || {}), ...cambios } }
                : prev
            );
          }}
        />
      )}
      {saldoUserId && (
        <AddCashModal
          active={!!saldoUserId}
          handleAddCashModal={() => setSaldoUserId(null)}
          venueID={venueID}
          token={token}
          user_id={saldoUserId}
          setErrorMessage={() => {}}
          onCashAdded={(uid, newBalance) => {
            updateUserCredit?.(uid, newBalance);
            if (String(atendiendo?.id) === String(uid)) {
              setAtendiendo((prev) => (prev ? { ...prev, credit_balance: newBalance } : prev));
            }
            setSaldoUserId(null);
          }}
          currentBalance={
            usersInVenue.find((u) => String(u.id) === String(saldoUserId))?.credit_balance ??
            usersInVenue.find((u) => String(u.id) === String(saldoUserId))?.profile?.credit_balance ??
            0
          }
          memberName={(() => {
            const m = usersInVenue.find((u) => String(u.id) === String(saldoUserId));
            const nom = `${m?.profile?.first_name || ""} ${m?.profile?.last_name || ""}`.trim();
            return nom || undefined;
          })()}
        />
      )}

      {/* ── Vincular móvil (QR de un solo uso, TTL corto) y Nueva entrada: los dos
          son el CustomModal del sistema (role="dialog", foco, telón, capa de
          modales). Antes un `fixed inset-0 z-50` con panel y sombra a mano cada
          uno (auditoría CRUO 21-09, CRUO-005; el mismo UI-009 que Entradas). */}
      <CustomModal
        active={Boolean(vinculo)}
        handleModal={() => setVinculo(null)}
        title="Vincular móvil"
        size="sm"
        titleId="vincular-movil-title"
      >
        {vinculo && (
          <div className="text-center">
            <p className="text-sm text-[var(--muted-foreground)]">
              {`${vinculo.socio?.profile?.first_name || ""} ${vinculo.socio?.profile?.last_name || ""}`.trim() || "Socio"}
              : escanea este QR con tu móvil.
            </p>
            <div className="mx-auto mt-4 w-fit rounded-lg border border-[var(--border)] bg-[var(--card)] p-3">
              <QRCode value={vinculo.url} size={200} />
            </div>
            <p className="mt-3 text-xs text-[var(--muted-foreground)]">Caduca en {vinculo.min || 5} minutos. Un solo uso en persona.</p>
            <Button size="lg" className="mt-4 w-full" onClick={() => setVinculo(null)}>
              Hecho
            </Button>
          </div>
        )}
      </CustomModal>

      <CustomModal
        active={showNueva}
        handleModal={() => setShowNueva(false)}
        title="Nueva entrada"
        size="sm"
        titleId="nueva-entrada-title"
      >
        {showNueva && (
          <>
            <input
              autoFocus
              value={nuevaQ}
              onChange={(e) => setNuevaQ(e.target.value)}
              placeholder="Nombre, nº de socio o email…"
              className="mb-3 h-10 w-full rounded-md border border-[var(--border)] px-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            />
            {!busquedaSuficiente(nuevaQ) && (
              <div className="mb-2 rounded-lg bg-[var(--muted)] px-3 py-2 text-xs text-[var(--muted-foreground)]">
                Escribe el <b>nº de socio</b>, o al menos <b>2 letras</b> del nombre o email.
              </div>
            )}
            {nuevaBuscando && busquedaSuficiente(nuevaQ) && (
              <div className="mb-2 px-1 text-xs text-[var(--muted-foreground)]">Buscando…</div>
            )}
            <div className="max-h-72 overflow-y-auto">
              {nuevaResults
                .filter((c) => !usersInVenue.some((u) => String(u.id) === String(c.id)))
                .map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => registrarEntrada(c)}
                    className="flex w-full items-center gap-3 rounded-sm border-b border-[var(--border)] px-2 py-2 text-left hover:bg-[var(--surface-subtle)]"
                  >
                    <MemberPhoto
                      path={c?.profile?.image_path || c?.profile?.documents_path}
                      name={c?.profile?.first_name || c?.email || ""}
                      className="h-9 w-9 rounded-lg object-cover"
                    />
                    <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                      <span className="truncate">
                        {c?.profile?.first_name} {c?.profile?.last_name}
                        <span className="ml-2 text-xs text-[var(--muted-foreground)]">{c?.email}</span>
                      </span>
                      <CaducadoChip socio={c} />
                    </span>
                    <span className="text-xs font-semibold text-[var(--primary)]">Entrar →</span>
                  </button>
                ))}
              {busquedaSuficiente(nuevaQ) && !nuevaBuscando && nuevaResults.length === 0 && (
                <div className="p-4 text-center text-sm text-[var(--muted-foreground)]">Sin resultados.</div>
              )}
              {busquedaSuficiente(nuevaQ) && !nuevaBuscando && nuevaResults.length > 0 &&
                nuevaResults.every((c) => usersInVenue.some((u) => String(u.id) === String(c.id))) && (
                <div className="p-4 text-center text-sm text-[var(--muted-foreground)]">
                  Los socios encontrados <b>ya están en sala</b>.
                </div>
              )}
            </div>
          </>
        )}
      </CustomModal>

      {/* ── Barra inferior del carrito, solo por debajo de lg (en lg el carrito es la
          columna fija de la derecha). Fija como en el Dispensario: a la izquierda
          respeta la barra lateral (md:left-64) y por debajo de md se sienta justo
          sobre la navegación móvil: su alto es 3.5rem + el mismo relleno inferior que
          ella (MobileBottomNav.jsx). z-40, como la barra del Dispensario: por debajo de
          menús y modales, por encima de las barras sticky de sección. */}
      {barraCarrito && (
        <div
          className="fixed bottom-[calc(3.5rem+max(0.25rem,env(safe-area-inset-bottom)))] left-0 right-0 z-40 md:bottom-0 md:left-64 lg:hidden"
          data-testid="sala-cart-bar"
        >
          <CartBar
            total={total}
            count={cart.items.length}
            credit={saldo}
            onViewCart={() => carritoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          />
        </div>
      )}
    </div>
  );
}
